import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import './Cart.css';

function Cart() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [updatingItems, setUpdatingItems] = useState({});

  const { data: cart, isLoading } = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const sessionId = localStorage.getItem('sessionId');
      const res = await apiClient.get('/cart', {
        headers: sessionId ? { 'x-session-id': sessionId } : {},
      });
      return res.data;
    },
  });

  const updateCartMutation = useMutation({
    mutationFn: async ({ id, quantity }) => {
      const sessionId = localStorage.getItem('sessionId');
      setUpdatingItems(prev => ({ ...prev, [id]: true }));
      return apiClient.put(`/cart/${id}`, { quantity }, {
        headers: sessionId ? { 'x-session-id': sessionId } : {},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cart']);
      setUpdatingItems({});
    },
    onError: () => {
      setUpdatingItems({});
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: async (id) => {
      const sessionId = localStorage.getItem('sessionId');
      return apiClient.delete(`/cart/${id}`, {
        headers: sessionId ? { 'x-session-id': sessionId } : {},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cart']);
    },
  });

  const handleQuantityChange = (itemId, currentQuantity, change) => {
    const newQuantity = Math.max(1, currentQuantity + change);
    updateCartMutation.mutate({ id: itemId, quantity: newQuantity });
  };

  if (isLoading) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-header">
            <h1>{t('cart')}</h1>
            <p className="cart-subtitle">{t('yourCart')}</p>
          </div>
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"></path>
              </svg>
            </div>
            <h2>{t('emptyCart')}</h2>
            <p>{t('emptyCartHint')}</p>
            <Link to="/products" className="empty-state-primary-btn">
              {t('continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // cart.total jau yra su PVM, jei kainos rodomos su PVM
  const totalWithTax = cart.total || 0;
  const shipping = cart.shipping_cost || 0;
  
  // Jei cart.tax yra 0 arba neegzistuoja, skaičiuoti iš cart.total
  let tax = cart.tax || 0;
  let subtotalWithoutTax = 0;
  
  if (tax > 0) {
    // Jei tax yra nurodytas, naudoti jį
    subtotalWithoutTax = totalWithTax - tax;
  } else {
    // Jei tax nėra nurodytas, skaičiuoti iš total (21% PVM)
    tax = totalWithTax * (21 / 121); // PVM iš sumos su PVM
    subtotalWithoutTax = totalWithTax - tax;
  }
  
  // Iš viso su PVM
  const total = totalWithTax + shipping;

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-header">
          <h1>{t('cart')}</h1>
          <p className="cart-subtitle">{t('yourCart')}</p>
        </div>

        <div className="cart-items-section">
          <h2 className="section-title">{t('product')}</h2>
          <div className="cart-items-list">
            {cart.items.map((item) => {
              const itemTotal = (item.price || 0) * (item.quantity || 0);
              const isUpdating = updatingItems[item.id];
              return (
                <div key={item.id} className="cart-item-card">
                  <Link to={`/products/${item.slug}`} className="cart-item-image">
                    {(item.variant_image || item.image) ? (
                      <img src={item.variant_image || item.image} alt={item.name} />
                    ) : (
                      <div className="image-placeholder">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                          <path d="M9 9h6v6H9z"></path>
                        </svg>
                      </div>
                    )}
                  </Link>
                  
                  <div className="cart-item-info">
                    <Link to={`/products/${item.slug}`}>
                      <h3 className="item-name">{item.name}</h3>
                    </Link>
                    {item.variant_name && (
                      <p className="variant-name">
                        <span className="variant-label">{t('variant')}:</span> {item.variant_name}
                      </p>
                    )}
                    {item.variant_attributes && item.variant_attributes.length > 0 && (
                      <div className="variant-attributes">
                        {item.variant_attributes.map((attr, idx) => (
                          <span key={idx} className="variant-attr">
                            {attr.attribute_name}: {attr.attribute_value}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="item-sku">SKU: {item.variant_sku || item.sku || 'N/A'}</p>
                  </div>

                  <div className="cart-item-quantity">
                    <div className="quantity-controls">
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(item.id, item.quantity, 1)}
                        disabled={isUpdating}
                        className="quantity-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14"></path>
                        </svg>
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const qty = Math.max(1, parseInt(e.target.value) || 1);
                          updateCartMutation.mutate({ id: item.id, quantity: qty });
                        }}
                        className="quantity-input"
                        disabled={isUpdating}
                      />
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(item.id, item.quantity, -1)}
                        disabled={isUpdating || item.quantity <= 1}
                        className="quantity-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="cart-item-total">
                    <span className="total-label">{t('total')}</span>
                    <div className="total-price-container">
                      <span className="total-amount">
                        {itemTotal.toFixed(2)} €
                      </span>
                      <span className="total-amount-without-vat">
                        {(itemTotal / 1.21).toFixed(2)} € {t('withoutVAT')}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => removeItemMutation.mutate(item.id)}
                    className="remove-item-btn"
                    disabled={removeItemMutation.isLoading}
                    title={t('remove')}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="cart-summary-section">
          <h2 className="summary-title">{t('cartSummary')}</h2>
          <div className="summary-list">
            <div className="summary-item">
              <span className="summary-label">{t('subtotal')}</span>
              <span className="summary-value">
                {subtotalWithoutTax.toFixed(2)} €
              </span>
            </div>
            {tax > 0 && (
              <div className="summary-item summary-tax">
                <span className="summary-label">{t('taxAmount')}</span>
                <span className="summary-value">
                  {tax.toFixed(2)} €
                </span>
              </div>
            )}
            {shipping > 0 && (
              <div className="summary-item summary-shipping">
                <span className="summary-label">{t('shippingCost')}</span>
                <span className="summary-value">
                  {shipping.toFixed(2)} €
                </span>
              </div>
            )}
            <div className="summary-item summary-total">
              <span className="summary-label">{t('total')}</span>
              <span className="summary-value">
                {total.toFixed(2)} €
              </span>
            </div>
            <div className="summary-actions">
              <button
                onClick={() => navigate('/checkout')}
                className="checkout-btn"
                disabled={removeItemMutation.isLoading || updateCartMutation.isLoading}
              >
                {t('checkout')}
              </button>
              <Link to="/products" className="continue-shopping">
                {t('continueShopping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;
